// Phase 7 D4 — Track D T-D6. PaymentsToolbar.
//
// Toolbar that sits at the top of /payments with four affordances:
//   - Add payment (primary)        — opens PaymentDialog (mode='add')
//   - Upload statement (secondary) — opens StatementUploadDialog
//   - Review (link with badge)     — navigates to /payments/review
//   - Recurring (link)             — navigates to /payments/recurring
//
// The Review link surfaces a count badge sourced from
// `usePaymentReviewCount`. When the count is zero (or unknown), the badge
// is omitted — reduces visual noise.
//
// Every affordance is reachable with Tab in declared order; focus rings
// are inherited from the Button + Link primitives' focus tokens.

import { Link } from 'react-router-dom';
import { copy } from '@dailydeck/shared/copy';
import { Button } from './ui/Button.js';
import { Badge } from './ui/Badge.js';
import { usePaymentReviewCount } from '../hooks/usePaymentReviewCount.js';

export interface PaymentsToolbarProps {
  onAdd: () => void;
  onUpload: () => void;
}

export function PaymentsToolbar({
  onAdd,
  onUpload,
}: PaymentsToolbarProps): JSX.Element {
  const reviewCount = usePaymentReviewCount();
  const count = reviewCount.isSuccess ? reviewCount.data : 0;

  return (
    <div
      role="toolbar"
      aria-label={copy.payments.toolbar.addAriaLabel}
      data-testid="payments-toolbar"
      className="flex flex-wrap items-center gap-2"
    >
      <Button
        type="button"
        variant="primary"
        onClick={onAdd}
        aria-label={copy.payments.toolbar.addAriaLabel}
        data-testid="payments-toolbar-add"
      >
        {copy.payments.toolbar.addLabel}
      </Button>
      <Button
        type="button"
        variant="secondary"
        onClick={onUpload}
        aria-label={copy.payments.toolbar.uploadAriaLabel}
        data-testid="payments-toolbar-upload"
      >
        {copy.payments.toolbar.uploadLabel}
      </Button>
      <div className="ml-auto flex items-center gap-2">
        <Button
          asChild
          variant="ghost"
          aria-label={copy.payments.toolbar.reviewAriaLabel}
          data-testid="payments-toolbar-review"
        >
          <Link to="/payments/review">
            <span>{copy.payments.toolbar.reviewLabel}</span>
            {count > 0 && (
              <Badge
                variant="accent"
                data-testid="payments-toolbar-review-badge"
                className="ml-1"
              >
                {count}
              </Badge>
            )}
          </Link>
        </Button>
        <Button
          asChild
          variant="ghost"
          aria-label={copy.payments.toolbar.recurringAriaLabel}
          data-testid="payments-toolbar-recurring"
        >
          <Link to="/payments/recurring">
            {copy.payments.toolbar.recurringLabel}
          </Link>
        </Button>
      </div>
    </div>
  );
}
